import React from "react";

/**
 * Classifier icons — scope-card style.
 *
 * Each icon is a mini oscilloscope viewport showing the *characteristic
 * waveform* of that signal class:
 *   - Brain   → smooth alpha sine
 *   - Muscle  → high-frequency EMG burst
 *   - Eye     → slow blink peak
 *   - Heart   → QRS complex
 *   - Line    → 60Hz square wave
 *   - Chan    → random jagged jumps
 *   - Other   → mixed spikes
 *   - Unsure  → dashed / incomplete trace
 *
 * Common chrome: scope corner brackets + faint dashed baseline, drawn in
 * `currentColor` so they tint with the parent button's text color.
 */

const ViewBox = "0 0 28 28";

const Scope = ({ children, stroke = "currentColor" }) => (
  <svg
    width="26"
    height="26"
    viewBox={ViewBox}
    fill="none"
    stroke={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Corner brackets (scope viewport) */}
    <g strokeWidth="1.4" opacity="0.55">
      <path d="M3 7 L3 3 L7 3" />
      <path d="M21 3 L25 3 L25 7" />
      <path d="M3 21 L3 25 L7 25" />
      <path d="M25 21 L25 25 L21 25" />
    </g>
    {/* Faint baseline */}
    <path
      d="M5 14 L23 14"
      strokeWidth="0.9"
      strokeDasharray="1.2 1.8"
      opacity="0.3"
    />
    {/* Signal trace */}
    <g strokeWidth="1.8">{children}</g>
  </svg>
);

// 1. Brain — smooth alpha sine
export const BrainIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 C 7 8, 9 8, 11 14 S 15 20, 17 14 S 21 8, 23 14" />
  </Scope>
);

// 2. Muscle — high-frequency EMG burst
export const MuscleIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L6 11 L7 17 L8 10 L9 18 L10 12 L12 17 L13 10 L14 18 L15 11 L16 17 L18 12 L19 16 L23 14" />
  </Scope>
);

// 3. Eye — slow blink artifact
export const EyeIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L10 14 L11.5 5 L13 14 L23 14" />
  </Scope>
);

// 4. Heart — textbook QRS complex (P, QRS, T)
export const HeartIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L7 14 L8 12.5 L9 14 L10 14 L11 15 L12 5 L13 22 L14 14 L16 14 L17 11.5 L18.5 14 L23 14" />
  </Scope>
);

// 5. Line Noise — 60Hz square wave
export const LineNoiseIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L5 9 L8 9 L8 19 L11 19 L11 9 L14 9 L14 19 L17 19 L17 9 L20 9 L20 19 L23 19 L23 14" />
  </Scope>
);

// 6. Chan Noise — random large jumps
export const ChanNoiseIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L7 6 L9 19 L11 10 L13 21 L15 6 L17 17 L19 8 L21 19 L23 12" />
  </Scope>
);

// 7. Other — mixed upward/downward spikes
export const OtherIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L9 14 L10 8 L12 14 L13 20 L14 14 L17 14 L19 9 L21 14 L23 14" />
  </Scope>
);

// 8. Unsure — dashed / incomplete trace (data dropouts)
export const UnsureIcon = (props) => (
  <Scope {...props}>
    <path d="M5 14 L7.5 14" />
    <path d="M9.5 14 Q 11.5 10 13.5 14" />
    <path d="M15.5 14 Q 17.5 18 19.5 14" />
    <path d="M21 14 L23 14" />
  </Scope>
);

export const CLASSIFIER_ICONS = {
  brain: BrainIcon,
  muscle: MuscleIcon,
  eye: EyeIcon,
  heart: HeartIcon,
  linenoise: LineNoiseIcon,
  channoise: ChanNoiseIcon,
  other: OtherIcon,
  unsure: UnsureIcon,
};
